/**
 * Slice 52, gap 1 — correcting a draft Meta refused **after** its campaign was
 * created, without touching anything that already exists at Meta.
 *
 * ## THE SITUATION THIS EXISTS FOR
 *
 * A publish creates the campaign, then the ad set, then per ad a video, a
 * creative and an ad. Meta can refuse part way. The commonest real case will
 * be a budget under Meta's minimum for the currency: the campaign exists,
 * paused, and the ad set is refused. Slice 42 left that draft uneditable — it
 * is the record of what exists — so the only way out was a new draft, and the
 * campaign already at Meta became an orphan nobody could see.
 *
 * Slice 52 makes the draft correctable **in place**, and nothing is orphaned,
 * because nothing new is created in place of what exists: a corrected draft
 * re-uses every id already recorded and the resume continues from them,
 * exactly as slice 42's resume already does.
 *
 * ## THE RULE, DECIDED FROM `published` ALONE
 *
 * {@link metaDraftCorrection} is the whole rule, and it reads only the ids on
 * file — never Meta, never a status, never a screen's opinion:
 *
 *   - `campaignId` set     → `draft.campaign` is frozen (name, objective,
 *                            special ad category).
 *   - `adSetId` set        → `draft.adSet` is frozen ENTIRE — name, budget kind
 *                            and amount, end date, targeting, placements — and
 *                            so is `pageId`, because the ad set carries the
 *                            promoted page's delivery shape with it.
 *   - any `creativeIds[i]` → `pageId` and `instagramAccountId` are frozen: a
 *                            creative is built against both, and a creative
 *                            that exists cannot be re-pointed from here.
 *   - per ad `i`, any of `videoIds[i]`, `creativeIds[i]`, `adIds[i]` set
 *                          → ad `i` is frozen whole.
 *
 * Everything else — the parts of the draft nothing at Meta has been made from
 * yet — is ordinary editing.
 *
 * ## WHY THE ADS LIST MAY ONLY GROW
 *
 * `published.videoIds`, `creativeIds` and `adIds` are indexed by the ad's
 * position in `draft.ads`. Remove an ad, or reorder two, and index `i` of the
 * arrays stops naming ad `i` of the draft — so the resume would create an ad
 * against a creative belonging to a different ad, and the money would run
 * against content nobody reviewed. Nothing on screen would show it. So while a
 * campaign is on file the list may be **appended to and nothing else**, and
 * every append extends the three arrays with nulls
 * ({@link metaPublishedExtendedTo}) so they stay the same length as the ads.
 *
 * ## WHY THIS CANNOT SPEND MONEY
 *
 * A correction changes the body of creates **not yet made**. It cannot reach an
 * updater — `metaGraphUpdate` and `metaGraphUpdateOnce` are not callable from
 * the drafts path, only `metaGraphPost`, which creates paused, makes one
 * attempt and never retries — and `status` is not a field this function can
 * write, because a draft has none: every create carries `META_CREATE_STATUS`,
 * typed as the literal `'PAUSED'`. A campaign a refusal left behind has no ad
 * set, or an ad set with no ads, and Meta bills delivery rather than budgets,
 * so its budget is a ceiling and never a charge. The correction bumps
 * `version`, so a Start confirmation taken before it is refused; and a
 * `published` draft is not correctable at all.
 *
 * ## WHAT THIS IS NOT
 *
 * It is not editing at Meta. Nothing here sends an update for an object that
 * exists — that is slice 53's row, and this design exists precisely so that
 * gap 1 does not need it. When the part that must change is one that already
 * exists (a campaign objective Meta will not accept beside the chosen
 * placements, say), the answer stays "start a new draft", and the refused
 * draft's page states the orphan as figures: the campaign id, the ad account,
 * and a link to it in Ads Manager.
 *
 * REJECTED: deciding the freeze from the draft's `status`. `failed` is not the
 * question — what exists at Meta is — and a status can be rewritten by a
 * reclaim while ids cannot. REJECTED: a boolean "editable" per draft. Half of
 * a refused draft is editable and half is not, and one flag cannot say which
 * half; the composer needs the field list to draw the "At Meta" chips, and the
 * API needs it to refuse per field.
 */
import type { MetaAdDraft, MetaPublishedIds } from './meta-ads-write.js';

/**
 * The one sentence a frozen field is refused with, on the field itself.
 *
 * "cannot be changed now" rather than "cannot be changed": the whole draft
 * becomes editable again nowhere, but a *new* draft can say anything, and the
 * page says so beside the orphan's ids.
 */
export const META_AT_META_REFUSAL = 'At Meta — this cannot be changed now.';

/** Removing an ad that exists at Meta. Lands on the ads list, not on one ad. */
export const META_AD_REMOVAL_REFUSAL = 'This ad exists at Meta and cannot be removed from the draft.';

/**
 * One field a correction may not change, as a path into the draft request —
 * the same dot path the composer's fields and `ValidationFailedError` use, so
 * a refusal lands on a drawn input and focus can move to the first.
 */
export interface MetaDraftCorrectionIssue {
  readonly path: string;
  readonly message: string;
}

/** Whether a correction is allowed at all, and what it may not touch. */
export interface MetaDraftCorrectionVerdict {
  /**
   * Whether any of the draft exists at Meta. `false` is slice 42's ordinary
   * edit: nothing is frozen and `frozen` is empty.
   */
  readonly atMeta: boolean;
  /** Every path the correction changed that it may not. Empty when it is allowed. */
  readonly issues: readonly MetaDraftCorrectionIssue[];
  /** The paths frozen by what is on file, changed or not — what the composer draws read-only. */
  readonly frozen: readonly string[];
}

const CAMPAIGN_FIELDS = ['name', 'objective', 'specialAdCategory'] as const;
const TARGETING_FIELDS = ['countries', 'ageMin', 'ageMax', 'gender', 'placements'] as const;

/** Deep equality over the JSON a draft is. Both sides came through zod, so both are plain JSON. */
function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

/**
 * What a correction of `current` into `next` may not do, given what exists at
 * Meta.
 *
 * Pure, and the only place the rule lives: the composer calls it to draw the
 * frozen fields read-only under an "At Meta" chip, so the refusal is prevented
 * rather than reported, and the API calls it to refuse anyway — for any request
 * the composer would not make. **The server-side call is not redundant.** No
 * test driven through the composer would ever send a frozen field, so the API
 * check has to be driven by requests the composer cannot make, one bad field
 * at a time.
 */
export function metaDraftCorrection(
  current: MetaAdDraft,
  next: MetaAdDraft,
  published: MetaPublishedIds,
): MetaDraftCorrectionVerdict {
  if (published.campaignId === null) return { atMeta: false, issues: [], frozen: [] };

  const issues: MetaDraftCorrectionIssue[] = [];
  const frozen: string[] = [];
  const freeze = (path: string, left: unknown, right: unknown): void => {
    frozen.push(path);
    if (!same(left, right)) issues.push({ path, message: META_AT_META_REFUSAL });
  };

  for (const field of CAMPAIGN_FIELDS) {
    freeze(`campaign.${field}`, current.campaign[field], next.campaign[field]);
  }

  /* A creative is built against the Page and the Instagram account, so one
     that exists freezes both. The ad set freezes the Page as well — it carries
     the promoted page's delivery shape — but never the Instagram account,
     which only a creative names. */
  const hasCreative = published.creativeIds.some((one) => one !== null);
  if (published.adSetId !== null || hasCreative) freeze('pageId', current.pageId, next.pageId);
  if (hasCreative) freeze('instagramAccountId', current.instagramAccountId, next.instagramAccountId);

  if (published.adSetId !== null) {
    freeze('adSet.name', current.adSet.name, next.adSet.name);
    freeze('adSet.budget.kind', current.adSet.budget.kind, next.adSet.budget.kind);
    freeze('adSet.budget.amount', current.adSet.budget.amount, next.adSet.budget.amount);
    freeze('adSet.endsOn', current.adSet.endsOn, next.adSet.endsOn);
    for (const field of TARGETING_FIELDS) {
      freeze(`adSet.targeting.${field}`, current.adSet.targeting[field], next.adSet.targeting[field]);
    }
  }

  /*
   * The ads. Index `i` of the three published arrays names ad `i` of the
   * draft, so an ad with any id on file is frozen where it stands, and the
   * list may only grow — see the header on what a removal or a reorder would
   * pair with what.
   */
  const atMetaAt = (index: number): boolean =>
    (published.videoIds[index] ?? null) !== null ||
    (published.creativeIds[index] ?? null) !== null ||
    (published.adIds[index] ?? null) !== null;

  /* Refused whatever was in the removed ads, not only when one of them had an
     id: the arrays are positional and the resume walks them by index, so a
     list that shrinks while a campaign is on file is a pairing nobody can
     check. Appending is the one change the list may take. */
  if (next.ads.length < current.ads.length) {
    issues.push({ path: 'ads', message: META_AD_REMOVAL_REFUSAL });
  }

  for (const [index, ad] of current.ads.entries()) {
    if (!atMetaAt(index)) continue;
    frozen.push(`ads.${String(index)}`);
    const after = next.ads[index];
    if (after === undefined) continue; // Already refused as a removal, above.
    if (!same(ad.name, after.name)) issues.push({ path: `ads.${String(index)}.name`, message: META_AT_META_REFUSAL });
    if (!same(ad.creative, after.creative)) {
      issues.push({ path: `ads.${String(index)}.creative`, message: META_AT_META_REFUSAL });
    }
  }

  return { atMeta: true, issues, frozen };
}

/**
 * `published` with its three positional arrays extended with nulls to
 * `adCount` — what an appended ad needs so that index `i` still names ad `i`.
 *
 * Never shortens: a shorter list is refused by {@link metaDraftCorrection}
 * before this is reached, and truncating here would drop the record of an
 * object that exists at Meta. A draft with no campaign on file is rewritten
 * with `metaPublishedNothing` instead — there is nothing to keep.
 */
export function metaPublishedExtendedTo(published: MetaPublishedIds, adCount: number): MetaPublishedIds {
  const extend = (ids: readonly (string | null)[]): (string | null)[] => [
    ...ids,
    ...Array.from({ length: Math.max(0, adCount - ids.length) }, () => null),
  ];
  return {
    adAccountId: published.adAccountId,
    campaignId: published.campaignId,
    adSetId: published.adSetId,
    videoIds: extend(published.videoIds),
    creativeIds: extend(published.creativeIds),
    adIds: extend(published.adIds),
  };
}

/**
 * Whether the three arrays line up with the draft's ads — the invariant a
 * misaligned correction breaks, asserted on every correction rather than
 * argued about in a comment.
 */
export function metaPublishedAligned(published: MetaPublishedIds, adCount: number): boolean {
  return (
    published.videoIds.length === adCount &&
    published.creativeIds.length === adCount &&
    published.adIds.length === adCount
  );
}

/*
 * `metaDraftAtMeta` — the orphan stated as figures for the draft's page —
 * stood here and was exported, and nothing outside its own probe ever called
 * it: the page it was written for reads `record.published` directly. Slice
 * 52's reviewer found it, and it is deleted rather than left exported, because
 * an exported function nothing calls is one the next reader trusts as load
 * bearing. Its doc also still named a field `holdsAdSet` that this file
 * renamed to `adSetId`, which is how long it had gone unread. If the draft
 * page ever wants those figures, it wants them with a caller.
 */
